import i18next from 'i18next';

/**
 * Returns the current locale determined by i18next configuration/state.
 * Priority:
 * 1) i18next.language (current language)
 * 2) i18next.resolvedLanguage (resolved language from detection)
 * 3) i18next.options.lng (initial configured language)
 * 4) Browser preferred language (navigator.languages / navigator.language)
 * 5) 'en' as a safe fallback
 */
export function getCurrentI18nLocale(localesList: {code: string }[]): string {
  // i18next.language is typically the active language (e.g., 'en', 'ja')

  const fromLanguage = i18next.language;

  // i18next.resolvedLanguage may be set after initialization/detection
  const fromResolved = (i18next as any).resolvedLanguage as string | undefined;

  // initial configured language in options
  const fromOptions = i18next.options?.lng as string | undefined;

  // Determine from browser preferred languages when available (on client side)
  const fromBrowser = getBrowserPreferredLocale();
  const resultCode = fromBrowser || fromLanguage || fromResolved || fromOptions || 'en';
  if(localesList){
    const item = localesList.find(item => item.code === resultCode )
    if(!item  || item.code) return 'en'

    return item.code;
  }

  return resultCode;
}

function getBrowserPreferredLocale(): string | undefined {
  // Ensure we're in a browser environment
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return undefined;

  // Supported languages per i18next configuration
  const supported = Object.keys((i18next.options as any)?.resources || {});

  // Helper to normalize a locale code to our supported set
  const normalize = (code: string): string | undefined => {
    if (!code) return undefined;
    const lower = code.toLowerCase();

    // reduce region-specific like en-US -> en
    const base = lower.split('-')[0];

    // Map special cases to available resource keys
    if (base === 'uk' || base === 'ua') return supported.includes('ua') ? 'ua' : undefined;
    if (base === 'us' || base === 'en') return supported.includes('en') ? 'en' : undefined;

    // direct match for other supported languages
    if (supported.includes(base)) return base;

    return undefined;
  };

  // Check navigator.languages first, then navigator.language
  const preferred = Array.isArray((navigator as any).languages)
    ? (navigator as any).languages as string[]
    : [];

  for (const lang of preferred) {
    const norm = normalize(lang);
    if (norm) return norm;
  }

  const single = (navigator as any).language as string | undefined;
  return single ? normalize(single) : undefined;
}

export default getCurrentI18nLocale;
